import { useCallback, useEffect, useState } from "react";
import { GroupCard } from "./GroupCard";
import SaveCohortModal from "./SaveCohortModal";
// import ExportDialog from "./ExportDialog";
import SavedCohortsDialog from "./SavedCohortsDialog";
import ParticipantDrawer from "./ParticipantDrawer";
import {
  GroupNode,
  createGroupNode,
  serializeDSL,
  deserializeDSL,
  countCriteria,
  countGroups,
  validateNode,
  canSwitchStudy,
} from "./nested-dsl-utils";
import { validateDSL } from "./dsl-validation";
import type {
  CohortCriteriaDSL,
  CohortMatchType,
  CohortPreviewLoadingMode,
  CohortPreviewResult,
} from "./types";
import { toastMessage } from "../../../utils/toast";
import { toastFn } from "../../../utils/toastfn";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import { FolderOpen, PlayArrowOutlined, Save } from "@mui/icons-material";
import { PreviewResults } from "./PreviewResults";
import http from "../../../utils/http";
import StudySelector from "./StudySelector";
import SaveCohortConfirmation from "./SaveCohortConfirmation";

const PREVIEW_PAGE_SIZE = 50;

interface NestedCriteriaBuilderProps {
  type: "registry" | "study";
  studyId?: string;
  studyName?: string;
  cohortName?: string;
  groupId?: string;
  cohortData?: any;
}

export function NestedCriteriaBuilder({
  type,
  studyId,
  studyName,
  cohortName,
  groupId,
  cohortData,
}: NestedCriteriaBuilderProps) {
  const [loading, setLoading] = useState(true);
  const [rootGroup, setRootGroup] = useState<GroupNode>(createGroupNode("AND"));
  const [previewResults, setPreviewResults] =
    useState<CohortPreviewResult | null>(null);
  const [previewLoadingMode, setPreviewLoadingMode] =
    useState<CohortPreviewLoadingMode | null>(null);
  // const [showSql, setShowSql] = useState(false);
  // const [copiedSql, setCopiedSql] = useState(false);
  // const [exportDialogOpen, setExportDialogOpen] = useState(false);
  // const [isExporting, setIsExporting] = useState(false);

  // Store the loaded cohort's configuration to preserve it
  const [cohortConfig, setCohortConfig] = useState<{
    scope?: "single-study" | "multi-study";
    output?: CohortCriteriaDSL["output"];
    name?: string;
    description?: string;
  }>({});

  // Save dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveStudyId, setSaveStudyId] = useState<{
    id: string;
    name: string;
  } | null>(studyId ? { id: studyId, name: studyName || "" } : null);
  const [savedCohortsDialogOpen, setSavedCohortsDialogOpen] = useState(false);
  // Track current cohort ID for delta tracking
  const [currentCohortId, setCurrentCohortId] = useState<string | undefined>();

  // Participant drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedParticipantId, setSelectedParticipantId] = useState<
    string | undefined
  >(undefined);

  // Save cohort modal state
  const [isSaving, setIsSaving] = useState(false);

  // Fetch near-match scores for saved cohorts
  // const { data: nearMatchScores = [] } = useQuery({
  //   queryKey: ["/api/cohorts", currentCohortId, "near-matches"],
  //   queryFn: async () => {
  //     const response = await fetch(
  //       `/api/cohorts/${currentCohortId}/near-matches?minScore=0.7`
  //     );
  //     if (!response.ok) throw new Error("Failed to fetch near-match scores");
  //     return await response.json();
  //   },
  //   enabled: !!currentCohortId,
  // });

  const handleStudyChange = (study: { id: string; name: string } | null) => {
    if (study) {
      if (canSwitchStudy(study.id, rootGroup)) {
        setSaveStudyId(study);
      } else {
        toastFn({
          title: "Cannot change study",
          description:
            "Please reset the criteria builder before changing the study.",
          variant: "warning",
        });
        return;
      }
    } else {
      setSaveStudyId(null);
    }
  };

  // Reset to empty group
  const handleReset = () => {
    setRootGroup(createGroupNode("AND"));
    setPreviewResults(null);
    setPreviewLoadingMode(null);
    toastMessage("success", "Reset Complete - All criteria have been cleared");
  };

  const handleRootGroupChange = (next: GroupNode) => {
    setRootGroup(next);
    setPreviewResults(null);
    setPreviewLoadingMode(null);
  };

  const runPreviewCall = async (opts: {
    matchType: CohortMatchType;
    cursor?: string;
    cohortId?: string;
  }) => {
    if (previewLoadingMode) return;
    const isReplace = !opts.cursor;
    try {
      setPreviewLoadingMode(isReplace ? "replace" : "append");
      if (isReplace) {
        setPreviewResults(null);
      }

      const validation = validateNode(rootGroup);
      if (!validation.valid) {
        throw new Error(validation.errors.join(", "));
      }

      const dsl = serializeDSL(rootGroup, {
        studyId: saveStudyId?.id || undefined,
        scope: saveStudyId?.id ? "single-study" : "multi-study",
        output: cohortConfig.output,
      });

      const dslValidation = validateDSL(dsl);
      if (!dslValidation.valid) {
        const errorMessages = dslValidation.errors
          .filter((e) => e.severity === "error")
          .map((e) => e.message)
          .join(", ");
        throw new Error(errorMessages);
      }

      const queryId = opts.cohortId || currentCohortId;

      const body = {
        ...dsl,
        ...(queryId && { cohortQueryId: queryId }),
        pagination: {
          size: PREVIEW_PAGE_SIZE,
          ...(opts.cursor && { cursor: opts.cursor }),
        },
        matchType: opts.matchType,
      };

      const url =
        type === "study"
          ? `/study/${studyId}/participant-groups/${groupId}/preview`
          : "/admin/registry/cohort-query/preview";
      const response = await http.post(url, body);

      const matches = response?.data?.data;
      setPreviewResults((prev) => {
        const nextPage = matches.page;
        const mergedData = isReplace
          ? nextPage.data
          : [...(prev?.page?.data ?? []), ...nextPage.data];
        return {
          exactMatchSql: matches.exactMatchSql,
          almostMatchSql: matches.almostMatchSql,
          cohortQueryId: matches.cohortQueryId,
          // BE only returns cohortRunId on the first page; keep the earlier value on later pages.
          cohortRunId: matches.cohortQueryRunId || prev?.cohortRunId,
          counts: matches.counts,
          page: { ...nextPage, data: mergedData },
        };
      });

      if (isReplace && opts.matchType === "exact") {
        toastFn({
          title: "Preview Complete",
          description: `Found ${matches.counts?.exact ?? 0} matching participants`,
          variant: "success",
        });
      }
    } catch (error) {
      toastFn({
        title: "Preview Failed",
        description: (error as Error).message,
        variant: "error",
      });
    } finally {
      setPreviewLoadingMode(null);
    }
  };

  const handlePreview = (cohortId?: string) =>
    runPreviewCall({ matchType: "exact", cohortId });

  const handlePreviewLoadMore = (
    matchType: CohortMatchType,
    cursor?: string,
  ) => runPreviewCall({ matchType, cursor });

  const handleConfirmSave = async (
    saveData: any,
    action: "create" | "update"
  ) => {
    try {
      setIsSaving(true);

      // Validate tree
      const validation = validateNode(rootGroup);
      if (!validation.valid) {
        toastFn({
          title: "Validation Error",
          description: validation.errors.join(", "),
          variant: "error",
        });
        return;
      }

      // Serialize to DSL
      const dsl = serializeDSL(rootGroup, {
        studyId: saveStudyId?.id || undefined,
        scope: saveStudyId?.id ? "single-study" : "multi-study",
        output: cohortConfig.output,
      });

      // Validate DSL
      const dslValidation = validateDSL(dsl);
      if (!dslValidation.valid) {
        const errorMessages = dslValidation.errors
          .filter((e) => e.severity === "error")
          .map((e) => e.message)
          .join(", ");
        toastFn({
          title: "DSL Validation Error",
          description: errorMessages,
          variant: "error",
        });
        return;
      }

      const payload = {
        name: saveData.name,
        description: saveData.description,
        dsl: dsl,
        studyId: saveStudyId?.id || undefined,
      };

      let response: any;
      if (type === "study") {
        response = await http.post(
          `/study/${studyId}/participant-groups/${groupId}/query`,
          payload
        );
        setCurrentCohortId(response?.data?.data?.id);
        toastFn({
          title: "Criteria Saved",
          description: "Criteria definition saved successfully",
          variant: "success",
        });
      } else {
        if (action === "update" && currentCohortId) {
          // Update existing cohort using PUT
          response = await http.patch(
            `/admin/registry/cohort-query/${currentCohortId}`,
            payload
          );
          toastFn({
            title: "Criteria Updated",
            description: "Criteria definition updated successfully",
            variant: "success",
          });
        } else {
          // Create new cohort using POST
          response = await http.post("/admin/registry/cohort-query", payload);
          setCurrentCohortId(response?.data?.data?.id);

          if (action === "create") {
            // Update cohort config for "Save and Create New"
            toastFn({
              title: "New Criteria Created",
              description: "New criteria definition created successfully",
              variant: "success",
            });
          } else {
            toastFn({
              title: "Criteria Saved",
              description: "Criteria definition saved successfully",
              variant: "success",
            });
          }
        }
      }

      setCohortConfig({
        name: saveData.name,
        description: saveData.description,
        scope: saveStudyId?.id ? "single-study" : "multi-study",
        output: cohortConfig.output,
      });
      setSaveDialogOpen(false);
      setIsSaving(false);

      // Auto-run preview after successful save, passing the cohort ID
      const savedCohortId = response?.data?.data?.id || currentCohortId;
      handlePreview(savedCohortId);
    } catch (err) {
      toastFn({
        title: "Save Failed",
        description: (err as Error).message,
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onLoadCohort = useCallback(
    (cohort: any) => {
      // Handle loading a saved cohort
      if (cohort.dsl) {
        const dsl = cohort.dsl as CohortCriteriaDSL;
        const tree = deserializeDSL(dsl, cohort.questions || []);
        setRootGroup(tree);
        setPreviewResults(null);
        setPreviewLoadingMode(null);
        setCurrentCohortId(cohort.id);
        setCohortConfig({
          scope: cohort?.dsl?.scope,
          output: cohort?.dsl?.output,
          name: cohort?.name || "",
          description: cohort?.description || "",
        });
        setSaveStudyId(
          cohort?.studyId
            ? { id: cohort.studyId, name: cohort.study?.name ?? "" }
            : null
        );

        if (type === "registry") {
          toastMessage(
            "success",
            `Loaded "${cohort.name}" - ${countCriteria(tree)} criteria loaded`
          );
        } else {
          setLoading(false);
        }
      }
    },
    [type]
  );

  useEffect(() => {
    if (type === "study" && cohortData) {
      setLoading(true);
      onLoadCohort(cohortData);
    } else {
      setLoading(false);
    }
  }, [cohortData, type, onLoadCohort]);

  // Handle export participants (all or selected)
  const handleExport = async (selectedParticipantIds?: string[]) => {
    if (!previewResults?.cohortQueryId || !previewResults?.cohortRunId) {
      toastFn({
        title: "Save Required",
        description: "Please save the cohort before exporting participants",
        variant: "warning",
      });
      return;
    }

    const participantIds = selectedParticipantIds || [];
    const isExportAll = participantIds.length === 0;

    if (selectedParticipantIds && selectedParticipantIds.length === 0) {
      toastFn({
        title: "Export Failed",
        description: "No participants selected",
        variant: "warning",
      });
      return;
    }

    try {
      const response = await http.post(
        `/admin/registry/cohort-query/${previewResults.cohortQueryId}/runs/${previewResults.cohortRunId}/export`,
        {
          participantIds,
        },
        {
          responseType: "blob",
        }
      );

      // Create a blob from the response
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `cohort-export-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toastFn({
        title: "Export Complete",
        description: isExportAll
          ? `Successfully exported all participants`
          : `Successfully exported ${
              participantIds.length
            } selected participant${participantIds.length > 1 ? "s" : ""}`,
        variant: "success",
      });
    } catch (error) {
      toastFn({
        title: "Export Failed",
        description: (error as Error).message,
        variant: "error",
      });
    }
  };

  // Get stats
  const criteriaCount = countCriteria(rootGroup);
  const groupCount = countGroups(rootGroup) - 1; // Exclude root group from count

  return loading ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <div>
      {/* Header Stats */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          wrap: "wrap",
          mb: 3,
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, wrap: "wrap" }}
        >
          <Typography variant="h2">
            {type === "study" ? cohortName : cohortConfig.name || "Untitled"}
          </Typography>
          <Chip
            label={`${criteriaCount} ${
              criteriaCount === 1 ? "criterion" : "criteria"
            }`}
            sx={{ mr: 1 }}
            variant={"outlined"}
            size="small"
          />
          {groupCount > 0 && (
            <Chip
              label={`${groupCount} ${groupCount === 1 ? "group" : "groups"}`}
              sx={{ mr: 1 }}
              variant={"outlined"}
              size="small"
            />
          )}
          {/* <Badge
            variant={selectedStudy ? "default" : "secondary"}
            className="gap-1"
          >
            {selectedStudy ? selectedStudy.name : "All Studies"}
          </Badge> */}
          {type === "registry" && (
            <StudySelector
              selectedStudy={saveStudyId}
              onStudyChange={handleStudyChange}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {type === "registry" && (
            <Button
              variant="outlined"
              onClick={() => setSavedCohortsDialogOpen(true)}
              data-testid="button-view-saved-cohorts"
              startIcon={<FolderOpen />}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Saved Criteria
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSaveDialogOpen(true)}
            disabled={criteriaCount === 0}
            data-testid="button-save-cohort"
            startIcon={<Save />}
          >
            Save
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => handlePreview()}
            disabled={criteriaCount === 0 || !!previewLoadingMode}
            data-testid="button-preview-nested-criteria"
            startIcon={<PlayArrowOutlined />}
          >
            {previewLoadingMode ? "Running..." : "Run"}
          </Button>
        </Box>
      </Box>

      {/* Criteria Tree */}
      <Box sx={{ mb: 3 }}>
        <GroupCard
          group={rootGroup}
          depth={0}
          onChange={handleRootGroupChange}
          showReset
          onReset={handleReset}
          studyId={saveStudyId?.id || ""}
          platformType={type}
        />
      </Box>

      {/* Preview Results with Tabs */}
      {(previewResults || previewLoadingMode) && (
        <PreviewResults
          previewResults={previewResults}
          loadingMode={previewLoadingMode}
          onExport={handleExport}
          onLoadMore={handlePreviewLoadMore}
          onParticipantClick={(participantId) => {
            setSelectedParticipantId(participantId);
            setDrawerOpen(true);
          }}
          type={type}
          cohortName={cohortName || ""}
          studyId={studyId || ""}
          groupId={groupId || ""}
        />
      )}

      {/* SQL Preview Section */}
      {/* {previewResults?.compiledSql && (
        <Card className="p-4">
          <button
            onClick={() => setShowSql(!showSql)}
            className="flex items-center justify-between w-full"
            data-testid="button-toggle-sql"
          >
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">
                Generated SQL Query
              </h3>
            </div>
            {showSql ? (
              <Badge variant="outline" className="text-xs">
                Hide
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs">
                Show
              </Badge>
            )}
          </button>
          {showSql && (
            <div className="mt-4 space-y-2">
              <div className="relative">
                <div className="max-h-[400px] overflow-auto border rounded-md bg-muted">
                  <pre className="p-4 text-xs font-mono">
                    <code className="text-foreground whitespace-pre">
                      {previewResults.compiledSql}
                    </code>
                  </pre>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      previewResults.compiledSql || ""
                    );
                    setCopiedSql(true);
                    setTimeout(() => setCopiedSql(false), 2000);
                    toastFn({
                      title: "SQL Copied",
                      description: "Query copied to clipboard",
                    });
                  }}
                  data-testid="button-copy-sql"
                >
                  {copiedSql ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                This SQL query is automatically generated from your criteria for
                developers to review and debug.
              </p>
            </div>
          )}
        </Card>
      )} */}

      {/* Save Dialog */}
      {/* Save Cohort Modal */}
      {saveDialogOpen && type === "registry" && (
        <SaveCohortModal
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          onSave={handleConfirmSave}
          isLoading={isSaving}
          allStudies={[]}
          loadedCohort={
            cohortConfig.name
              ? {
                  id: currentCohortId || "",
                  name: cohortConfig.name,
                  description: cohortConfig.description || "",
                }
              : null
          }
        />
      )}

      {saveDialogOpen && type === "study" && (
        <SaveCohortConfirmation
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          onConfirm={handleConfirmSave}
          isLoading={isSaving}
          cohortName={cohortName || ""}
        />
      )}

      {/* Participant Drawer */}
      {drawerOpen && (
        <ParticipantDrawer
          participantId={selectedParticipantId}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          type={type}
          studyId={studyId}
        />
      )}

      {/* Saved Cohorts Dialog */}
      {savedCohortsDialogOpen && (
        <SavedCohortsDialog
          open={savedCohortsDialogOpen}
          onClose={() => setSavedCohortsDialogOpen(false)}
          onLoadCohort={onLoadCohort}
        />
      )}

      {/* Export Dialog */}
      {/* {exportDialogOpen && (
        <ExportDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          onExport={handleExport}
          availableForms={[]}
          isExporting={isExporting}
        />
      )} */}
    </div>
  );
}
